<template>
  <Spinner v-if="isLoadingConsignmentList"/>

  <section v-else>
    <h2 v-if="!consignmentList.length">
      Партий нет
    </h2>

    <div v-else>
      <div class="between ai-c mb-30">
        <h2>Партии</h2>
        <div class="flex gap-s">
          <VButton
            v-if="permissionExportXslConsignment && !isMobile"
            :text="!isMobile ? 'Экспорт в XLS' : ''"
            icon="export"
            size="sm"
            @click.native="openModal('export-list')"
          />
          <VButton
            v-if="!isMobile"
            text="Настройки"
            size="sm"
            :icon="{ name: 'filter', width: '18px', height: '18px' }"
            @click.native="openModal('filter-setting')"
          />
          <div class="flex gap-xxs" v-if="!isMobile">
            <VButton
              size="sm"
              class="btn--no-shadow-always"
              icon="viewtype-cards"
              :active="viewType === 'cards'"
              @click.native="changeViewType()"
            />
            <VButton
              size="sm"
              class="btn--no-shadow-always"
              icon="viewtype-table"
              :active="viewType === 'table'"
              @click.native="changeViewType('table')"
            />
          </div>
        </div>
      </div>

      <div class="consignment-card-list consignment-list-box" v-if="viewType === 'cards' || isMobile">
        <ConsignmentCard
          v-for="consignment in consignmentList"
          :key="consignment.id"
          :consignment="consignment"
          is-consignments-list
          @goStageNext="consignmentGoStageNextHandler"
          @goSplitPrint="consignmentGoSplitPrintHandler"
          @remove="consignmentRemoveHandler"
          @update="consignmentUpdateHandler"
          @addDirtyWeight="consignmentAddDirtyWeightHandler"
          @addExecutor="consignmentAddExecutorHandler"
          @workTime="workTimeHandler"
          @setIndicators="setIndicatorsHandler"
        />
      </div>

      <ConsignmentTable
        v-else
        :columns="tableColumnsCurrent"
        :items="consignmentList"
        :loading="loadingTable"
        class="table_consignment custom-scroll"
        rowActive
        @sorted="sortedHandler"
        @goStageNext="consignmentGoStageNextHandler"
        @goSplitPrint="consignmentGoSplitPrintHandler"
        @remove="consignmentRemoveHandler"
        @update="consignmentUpdateHandler"
        @addExecutor="consignmentAddExecutorHandler"
        @resetSort="resetSortHandler"
        @workTime="workTimeHandler"
        @setIndicators="setIndicatorsHandler"
      />

      <AppPagination
        v-if="pagination.pageCount > 1"
        :current-page="pagination.page"
        :page-count="pagination.pageCount"
        @setPage="setCurrentRouteQueryPage"
      />
    </div>

    <ModalConsignmentSettingsTable
      v-if="isModal === 'filter-setting'"
      class="modal-consignment-settings"
      :table-columns-current="tableColumnsCurrent"
      @reject="closeModal"
    />
    <ModalConsignmentExportList
      v-if="isModal === 'export-list'"
      class="modal-consignment-export-list modal--xxl"
      @reject="closeModal"
    />
  </section>
</template>

<script>
import AppPagination from '@/components/ui/AppPagination'
import ConsignmentCard from '@/components/production/consignment/ConsignmentCard'

import { mixinRegisterListPage } from '@/mixins/views/Production/Order/mixinRegisterListPage'
import { mixinModal } from '@/mixins/modal/mixinModal'
import { mixinModalHandler } from '@/mixins/modal/MixinModalHandler'

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import VButton from '@/components/simple/button/VButton.vue'
import { ConsignmentTable } from '@/entities/order'
import ModalConsignmentSettingsTable from '@/components/ui/Modal/Production/Consignment/ModalConsignmentSettingsTable.vue'
import ModalConsignmentExportList from '@/components/ui/Modal/Production/Consignment/ModalConsignmentExportList.vue'

export default {
  components: {
    ModalConsignmentExportList,
    ModalConsignmentSettingsTable,
    ConsignmentTable,
    VButton,
    AppPagination,
    ConsignmentCard
  },

  mixins: [
    mixinRegisterListPage,
    mixinModal,
    mixinModalHandler
  ],
  props: {
    stageLine: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    loadingTable: false,
    sortTable: '-id'
  }),

  computed: {
    ...mapState('registerPage', {
      viewType: 'consignmentView'
    }),
    ...mapState('consignment', {
      consignmentList: 'consignmentList',
      isLoadingConsignmentList: 'isLoadingConsignmentList',
      pagination: 'pagination'
    }),
    ...mapGetters({
      tableColumnsCurrent: 'consignment/tableColumnsCurrent'
    }),
    ...mapGetters('permission', {
      permissionExportXslConsignment: 'permissionExportXslConsignment'
    }),
    ...mapState('mobile', {
      isMobile: 'isMobile'
    })
  },

  methods: {
    ...mapMutations('consignment', {
      REMOVE_CONSIGNMENT: 'REMOVE_CONSIGNMENT',
      UPDATE_CONSIGNMENT: 'UPDATE_CONSIGNMENT',
      CLEAR_STATE: 'CLEAR_STATE',
      SET_SORT: 'SET_SORT',
      RESET_SORT: 'RESET_SORT'
    }),
    ...mapMutations('warehouse', ['COUNT_CALC_BY_ARTICLE']),
    ...mapActions('consignment', {
      fetchConsignmentListData: 'fetchConsignmentListData',
      loadPage: 'loadPage',
      updateConsignment: 'updateConsignment'
    }),
    ...mapMutations('registerPage', {
      SET_CONSIGNMENT_VIEW: 'SET_CONSIGNMENT_VIEW'
    }),

    async consignmentAddDirtyWeightHandler (data) {
      this.UPDATE_CONSIGNMENT(data)
    },

    async consignmentGoStageNextHandler (data) {
      this.UPDATE_CONSIGNMENT(data)
      await this.fetchConsignmentListData()
    },

    async consignmentGoSplitPrintHandler (data) {
      this.UPDATE_CONSIGNMENT(data)
      await this.fetchConsignmentListData()
    },

    async consignmentAddExecutorHandler (data) {
      this.UPDATE_CONSIGNMENT(data)
    },

    async consignmentRemoveHandler (consignment) {
      this.REMOVE_CONSIGNMENT(consignment.id)
      this.COUNT_CALC_BY_ARTICLE(consignment)
    },

    async consignmentUpdateHandler (data, handlers) {
      const callback = async () => {
        await this.updateConsignment(data)
      }

      await this.modalHandler(handlers, callback)
    },

    changeViewType (type = 'cards') {
      this.SET_CONSIGNMENT_VIEW(type)
    },

    async sortedHandler (data) {
      data.sortDesc = !data.sortDesc
      this.SET_SORT((data.sortDesc ? '-' : '') + (data.sort || data.name || 'id'))
      await this.fetchConsignmentListData()
    },

    async resetSortHandler () {
      this.RESET_SORT()
      this.SET_SORT('-id')
      await this.fetchConsignmentListData()
    },

    async workTimeHandler () {
      await this.fetchConsignmentListData()
    },

    async setIndicatorsHandler () {
      await this.fetchConsignmentListData()
    }
  }
}
</script>
